import { useState, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import SEO from '../components/SEO'
import { blogPosts, categories } from '../data/blogData'

const Blog = () => {
  const [selectedCategoryId, setSelectedCategoryId] = useState('all')

  const filteredPosts = useMemo(() => {
    if (selectedCategoryId === 'all') return blogPosts
    return blogPosts.filter((post) => post.category === selectedCategoryId)
  }, [selectedCategoryId])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div>
      <SEO 
        title="MindVista Blog | Insights on Tech, AI & Web Dev" 
        description="Stay updated with the latest in web development, artificial intelligence, mobile apps, and business strategies from the MindVista team." 
      />

      {/* Hero Section */}
      <section className="py-16 md:py-24 relative overflow-hidden bg-gradient-to-br from-[#141414] via-[#121212] to-yellow-50/10">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-10 w-64 h-64 bg-[rgb(249,193,37)]/10 rounded-full blur-3xl"></div>
        </div>
        <div className="container-main relative z-10 text-center">
          <span className="section-kicker">Our Blog</span>
          <h1 className="mt-4 text-4xl font-bold md:text-6xl text-white">Latest Insights & News</h1>
          <p className="mt-4 text-lg text-[#adadad] max-w-2xl mx-auto">
            Deep dives, tutorials, and strategic insights into modern software development and digital transformation.
          </p>
        </div>
      </section>

      {/* Filter and Grid Section */}
      <section className="py-12 pb-24">
        <div className="container-main space-y-10">
          
          {/* Categories */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => {
              const active = category.id === selectedCategoryId
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setSelectedCategoryId(category.id)}
                  className={`inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    active
                      ? 'border-[rgb(249,193,37)] bg-[rgb(249,193,37)] text-[#101010] shadow-[0_0_15px_rgba(249,193,37,0.3)]'
                      : 'border-white/10 bg-[#171717] text-white/80 hover:border-[rgb(249,193,37)]/50 hover:text-[rgb(249,193,37)] hover:bg-[#1a1a1a]'
                  }`}
                >
                  {category.label}
                </button>
              )
            })}
          </div>

          {/* Featured Post (First Post) */}
          {selectedCategoryId === 'all' && filteredPosts.length > 0 && (
            <Link 
              to={`/blog/${filteredPosts[0].slug}`}
              className="group relative block w-full overflow-hidden rounded-3xl border border-white/10 text-left bg-[#101010] transition-all duration-500 hover:border-[rgb(249,193,37)]/50 hover:shadow-[0_0_30px_rgba(249,193,37,0.1)]"
            >
              <div className="grid lg:grid-cols-2">
                <div className="relative h-64 lg:h-[450px] overflow-hidden">
                  <img
                    src={filteredPosts[0].image}
                    alt={filteredPosts[0].title}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#101010] to-transparent lg:bg-gradient-to-r" />
                  <div className="absolute top-4 left-4">
                    <span className="rounded-full bg-[rgb(249,193,37)] px-3 py-1 text-xs font-bold text-[#101010]">
                      Featured
                    </span>
                  </div>
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <div className="flex items-center gap-3 text-sm text-[#888888]">
                    <span>{new Date(filteredPosts[0].date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    <span>•</span>
                    <span>{filteredPosts[0].readTime}</span>
                  </div>
                  <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl group-hover:text-[rgb(249,193,37)] transition-colors duration-300">
                    {filteredPosts[0].title}
                  </h2>
                  <p className="mt-4 text-base text-[#aaaaaa] leading-relaxed">
                    {filteredPosts[0].excerpt}
                  </p>
                  <div className="mt-8 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-[#222] border border-white/10 flex items-center justify-center text-white font-bold text-sm">
                      {filteredPosts[0].author.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-white">{filteredPosts[0].author}</p>
                    </div>
                  </div>
                </div>
              </div>
            </Link>
          )}

          {/* Posts Grid */}
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {(selectedCategoryId === 'all' ? filteredPosts.slice(1) : filteredPosts).map((post) => {
               const cat = categories.find(c => c.id === post.category)
               return (
                <Link
                  key={post.id}
                  to={`/blog/${post.slug}`}
                  className="group flex flex-col overflow-hidden rounded-3xl border border-white/10 bg-[#121212] transition-all duration-300 hover:-translate-y-1 hover:border-[rgb(249,193,37)]/40 hover:shadow-xl"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="rounded-full bg-[#1a1a1a]/80 backdrop-blur-md border border-white/10 px-3 py-1 text-[11px] font-semibold text-white">
                        {cat?.label || post.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="flex items-center gap-2 text-xs text-[#777777] mb-3">
                      <span>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      <span>•</span>
                      <span>{post.readTime}</span>
                    </div>
                    <h3 className="text-xl font-bold text-white group-hover:text-[rgb(249,193,37)] transition-colors duration-300 line-clamp-2">
                      {post.title}
                    </h3>
                    <p className="mt-3 flex-1 text-sm text-[#aaaaaa] line-clamp-3 leading-relaxed">
                      {post.excerpt}
                    </p>
                    <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
                       <span className="text-xs font-medium text-[#888888]">{post.author}</span>
                       <span className="text-sm font-semibold text-[rgb(249,193,37)] flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                          Read More 
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                       </span>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>

          {filteredPosts.length === 0 && (
            <div className="rounded-3xl border border-white/10 bg-[#121212] p-12 text-center">
              <div className="mx-auto h-16 w-16 text-white/20 mb-4">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" /></svg>
              </div>
              <h3 className="text-xl font-semibold text-white">No posts found</h3>
              <p className="mt-2 text-[#888888]">Try selecting a different category.</p>
            </div>
          )}

        </div>
      </section>
    </div>
  )
}

export default Blog
